import {
  getClubsByInstructor,
  getClubsByManager,
  getClubsByOwner,
} from '@/api/clubs/clubApi';
import { CLUB_FIELDS } from '@/api/clubs/clubs-fields.config';
import { Button } from '@/components/ui/button';
import { ClubSection } from '@/components/clubs/clubs-list/ClubSection';
import { ClubSkeleton } from './ClubSkeleton';
import { handleGlobalError } from '@/utils/error-handler';
import { useAuth } from '@/hooks/useAuth';
import { type Club } from '@/types/club.model';
import { Building2, Crown, GraduationCap, Users } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const skeletonRows = [0, 1, 2];

export const ClubList: React.FC = () => {
  const [ownedClubs, setOwnedClubs] = useState<Club[]>([]);
  const [managedClubs, setManagedClubs] = useState<Club[]>([]);
  const [instructedClubs, setInstructedClubs] = useState<Club[]>([]);
  const [loadingOwned, setLoadingOwned] = useState(true);
  const [loadingManaged, setLoadingManaged] = useState(true);
  const [loadingInstructed, setLoadingInstructed] = useState(true);
  const { hasOwnerRole, hasManagerRole, hasInstructorRole, isFutureOwner } = useAuth();
  const canSeeOwnedClubs = hasOwnerRole || isFutureOwner;
  const navigate = useNavigate();

  // `silent` refreshes in place after a row-level mutation: swapping the section
  // for skeletons would remount every row over a single club's change.
  const fetchOwnedClubs = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingOwned(true);
      const data = await getClubsByOwner(CLUB_FIELDS.FULL);
      setOwnedClubs(data);
    } catch (error) {
      console.error('Error fetching owned clubs: ', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudieron cargar los clubs propios. Por favor, intente nuevamente más tarde.',
      );
    } finally {
      if (!silent) setLoadingOwned(false);
    }
  }, []);

  const refreshOwnedClubs = useCallback(
    () => fetchOwnedClubs({ silent: true }),
    [fetchOwnedClubs],
  );

  const fetchManagedClubs = useCallback(async () => {
    try {
      setLoadingManaged(true);
      const data = await getClubsByManager(CLUB_FIELDS.FULL);
      setManagedClubs(data);
    } catch (error) {
      console.error('Error fetching managed clubs: ', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudieron cargar los clubs gestionados. Por favor, intente nuevamente más tarde.',
      );
    } finally {
      setLoadingManaged(false);
    }
  }, []);

  const fetchInstructedClubs = useCallback(async () => {
    try {
      setLoadingInstructed(true);
      const data = await getClubsByInstructor(CLUB_FIELDS.FULL);
      setInstructedClubs(data);
    } catch (error) {
      console.error('Error fetching instructed clubs: ', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudieron cargar los clubs donde das clases. Por favor, intente nuevamente más tarde.',
      );
    } finally {
      setLoadingInstructed(false);
    }
  }, []);

  useEffect(() => {
    if (canSeeOwnedClubs) {
      fetchOwnedClubs();
    } else {
      setLoadingOwned(false);
    }
    if (hasManagerRole) {
      fetchManagedClubs();
    } else {
      setLoadingManaged(false);
    }
    if (hasInstructorRole) {
      fetchInstructedClubs();
    } else {
      setLoadingInstructed(false);
    }
  }, [
    canSeeOwnedClubs,
    hasManagerRole,
    hasInstructorRole,
    fetchOwnedClubs,
    fetchManagedClubs,
    fetchInstructedClubs,
  ]);

  const allDoneLoading =
    (!canSeeOwnedClubs || !loadingOwned) &&
    (!hasManagerRole || !loadingManaged) &&
    (!hasInstructorRole || !loadingInstructed);
  const isEmpty =
    ownedClubs.length === 0 &&
    managedClubs.length === 0 &&
    instructedClubs.length === 0;

  // Global empty state
  if (allDoneLoading && isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Building2 size={40} className="mb-4 text-gc-muted" />
        <h3 className="mb-1 text-[16px] font-bold text-gc-ink">
          Todavía no tenés clubes
        </h3>
        <p className="mb-6 text-[13.5px] text-gc-muted">
          Cuando seas dueño, manager o profesor de un club, vas a verlo acá.
        </p>
        {canSeeOwnedClubs && (
          <Button onClick={() => navigate('/clubs/create')}>Nuevo club</Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[26px]">
      {/* Owned section */}
      {canSeeOwnedClubs && (
        <>
          {loadingOwned ? (
            <div className="flex flex-col gap-3">
              {skeletonRows.map((i) => (
                <ClubSkeleton key={i} />
              ))}
            </div>
          ) : (
            ownedClubs.length > 0 && (
              <ClubSection
                title="Clubes de tu propiedad"
                icon={Crown}
                accent="#C8922A"
                note="Control total: canchas, equipo y configuración."
                clubs={ownedClubs}
                role={hasOwnerRole ? 'owner' : 'future_owner'}
                onClubChanged={refreshOwnedClubs}
              />
            )
          )}
        </>
      )}

      {/* Managed section */}
      {hasManagerRole && (
        <>
          {loadingManaged ? (
            <div className="flex flex-col gap-3">
              {skeletonRows.map((i) => (
                <ClubSkeleton key={i} />
              ))}
            </div>
          ) : (
            managedClubs.length > 0 && (
              <ClubSection
                title="Donde sos manager"
                icon={Users}
                accent="#1F66D9"
                note="Permisos de manager — sin control de propietario."
                clubs={managedClubs}
                role="manager"
              />
            )
          )}
        </>
      )}

      {/* Instructor section */}
      {hasInstructorRole && (
        <>
          {loadingInstructed ? (
            <div className="flex flex-col gap-3">
              {skeletonRows.map((i) => (
                <ClubSkeleton key={i} />
              ))}
            </div>
          ) : (
            instructedClubs.length > 0 && (
              <ClubSection
                title="Donde das clases"
                icon={GraduationCap}
                accent="#1F8A5B"
                note="Permisos de profesor — podés dar clases en estos clubes."
                clubs={instructedClubs}
                role="instructor"
              />
            )
          )}
        </>
      )}
    </div>
  );
};
